import React, { useEffect, useCallback, useRef } from 'react';
import classNames from 'classnames';
import { useHistory, useLocation } from 'react-router-dom';
import debounce from 'lodash/debounce';

import { propTypes } from '../../../util/types';
import { useIntl } from '../../../util/reactIntl';
import { ListingCard, PaginationLinks, IconSearch } from '../../../components';

import css from './SearchResultsPanel.module.css';

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {propTypes.pagination} props.pagination - The pagination
 * @param {Object} props.search - The search
 * @param {Function} props.setActiveListing - The function to handle the active listing
 * @param {boolean} [props.isMapVariant] - Whether the map variant is enabled
 * @param {Object} [props.currentUser] - Current user for favorites functionality
 * @param {Function} [props.onToggleFavorites] - Function to toggle favorites
 * @param {Function} [props.onLoadMore] - Function to load more listings for infinite scroll
 * @param {boolean} [props.hasNextPage] - Whether there are more pages to load
 * @param {boolean} [props.isLoading] - Whether currently loading more listings
 * @param {Array} [props.pendingFavorites] - Array of listing IDs that are pending to be favorited
 * @returns {JSX.Element}
 */
const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings = [],
    pagination,
    search,
    setActiveListing,
    isMapVariant = true,
    currentUser,
    onToggleFavorites,
    onLoadMore,
    hasNextPage = false,
    isLoading = false,
    pendingFavorites = [],
  } = props;

  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const currentKeywords = searchParams.get('keywords') || '';

  const observerRef = useRef();
  const loadingRef = useRef();
  const inputRef = useRef();

  const handleSearchSubmit = e => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const keywords = formData.get('keywords');
    const params = new URLSearchParams(location.search);
    if (keywords) {
      params.set('keywords', keywords);
    } else {
      params.delete('keywords');
    }
    history.push(`${location.pathname}?${params.toString()}`);
  };

  // Infinite scroll logic
  const handleIntersection = useCallback(
    entries => {
      const [entry] = entries;
      if (entry.isIntersecting && hasNextPage && !isLoading && onLoadMore) {
        onLoadMore();
      }
    },
    [hasNextPage, isLoading, onLoadMore]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      threshold: 0.1,
      rootMargin: '100px',
    });

    if (loadingRef.current) {
      observer.observe(loadingRef.current);
    }

    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleIntersection]);
  const classes = classNames(rootClassName || css.root, className);

  const paginationLinks =
    pagination && pagination.totalPages >= 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName="SearchPage"
        pageSearchParams={search}
        pagination={pagination}
      />
    ) : null;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 767px) 100vw',
        `(max-width: 1023px) ${panelMediumWidth}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ');
    } else {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 549px) 100vw',
        '(max-width: 767px) 50vw',
        `(max-width: 1439px) 26vw`,
        `(max-width: 1920px) 18vw`,
        `14vw`,
      ].join(', ');
    }
  };

  return (
    <div className={classes}>
      {/* <div className={css.searchContainer}>
        <form onSubmit={handleSearchSubmit} className={css.searchForm}>
          <span className={css.searchIcon}>
            <IconSearch />
          </span>
          <input
            name="keywords"
            type="text"
            className={css.searchInput}
            placeholder={intl.formatMessage({ id: 'SearchResultsPanel.searchPlaceholder' })}
            defaultValue={currentKeywords}
          />
        </form>
      </div> */}
      <div className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
        {listings.map(l => (
          <ListingCard
            className={css.listingCard}
            key={l.id.uuid}
            listing={l}
            renderSizes={cardRenderSizes(isMapVariant)}
            setActiveListing={setActiveListing}
            currentUser={currentUser}
            onToggleFavorites={onToggleFavorites ? onToggleFavorites(l.id.uuid) : undefined}
            pendingFavorites={pendingFavorites}
          />
        ))}
        {props.children}

        {/* Infinite scroll trigger element */}
        {hasNextPage && (
          <div
            ref={loadingRef}
            className={css.loadingTrigger}
            style={{ height: '20px', margin: '20px 0' }}
          />
        )}
      </div>
      {!onLoadMore && paginationLinks}
    </div>
  );
};

export default SearchResultsPanel;
